/** Entspricht `@media (min-width: 1280px)` für `.eh-home-board--cols-*`. */
export const HOME_BOARD_WIDE_MQ = "(min-width: 1280px)";

export type HomeBoardPlacement = {
  col: number;
  span: number;
};

export type HomeBoardPosition = {
  top: number;
  left: number;
  width: number;
  visible: boolean;
};

/**
 * Spalten wie ein CSS-Grid mit `grid-auto-flow: row` (nicht dense).
 * Nur Reihenfolge und Breite entscheiden, nie die Höhe. Dadurch bleibt
 * die gespeicherte Anordnung stehen, wenn eine Kachel nachlädt.
 */
export function assignHomeBoardColumns(
  spans: readonly number[],
  columnCount: number,
): HomeBoardPlacement[] {
  const cols = Math.max(1, Math.floor(columnCount) || 1);
  const occupied: boolean[][] = [];
  const placements: HomeBoardPlacement[] = [];
  let cursorRow = 0;
  let cursorCol = 0;

  const isFree = (row: number, col: number, span: number): boolean => {
    if (col < 0 || span < 1 || col + span > cols) return false;
    const line = occupied[row];
    if (!line) return true;
    for (let c = col; c < col + span; c += 1) {
      if (line[c]) return false;
    }
    return true;
  };

  const mark = (row: number, col: number, span: number) => {
    const line = occupied[row] ?? (occupied[row] = []);
    for (let c = col; c < col + span; c += 1) line[c] = true;
  };

  for (const raw of spans) {
    const span = Math.min(cols, Math.max(1, Math.floor(raw) || 1));
    let row = cursorRow;
    let col = cursorCol;
    let guard = 0;
    const guardLimit = spans.length * cols + cols + 1;
    while (!isFree(row, col, span)) {
      col += 1;
      if (col + span > cols) {
        row += 1;
        col = 0;
      }
      guard += 1;
      if (guard > guardLimit) break;
    }
    mark(row, col, span);
    placements.push({ col, span });
    const nextCol = col + span;
    if (nextCol >= cols) {
      cursorRow = row + 1;
      cursorCol = 0;
    } else {
      cursorRow = row;
      cursorCol = nextCol;
    }
  }

  return placements;
}

/**
 * Setzt jede Kachel direkt unter die vorherige in denselben Spalten.
 * Eine höhere Nachbarspalte schiebt die anderen Spalten nicht nach unten.
 */
export function homeBoardPositions(
  heights: readonly number[],
  placements: readonly HomeBoardPlacement[],
  columnCount: number,
  colWidth: number,
  gap: number,
): { positions: HomeBoardPosition[]; totalHeight: number } {
  const cols = Math.max(1, Math.floor(columnCount) || 1);
  if (colWidth <= 0) {
    return {
      positions: heights.map(() => ({ top: 0, left: 0, width: 0, visible: false })),
      totalHeight: 0,
    };
  }

  const bottoms = Array.from({ length: cols }, () => 0);
  const positions = heights.map((rawHeight, index) => {
    const place = placements[index];
    if (!place) return { top: 0, left: 0, width: 0, visible: false };
    const span = Math.min(cols, Math.max(1, place.span));
    const col = Math.min(Math.max(0, place.col), cols - span);
    const height = rawHeight > 0 ? rawHeight : 0;
    let top = 0;
    for (let c = col; c < col + span; c += 1) {
      top = Math.max(top, bottoms[c] ?? 0);
    }
    const bottom = top + height + (height > 0 ? gap : 0);
    for (let c = col; c < col + span; c += 1) bottoms[c] = bottom;
    return {
      top,
      left: col * (colWidth + gap),
      width: span * colWidth + Math.max(0, span - 1) * gap,
      visible: true,
    };
  });

  const totalHeight = Math.max(0, ...bottoms.map((value) => (value > 0 ? value - gap : 0)));
  return { positions, totalHeight };
}

/** `--eh-home-board-gap` (rem oder px) in Pixel. rem bezieht sich auf die Root-Schriftgröße. */
export function parseHomeBoardGap(raw: string, fontSizePx: number): number {
  const font = fontSizePx > 0 ? fontSizePx : 16;
  const value = raw.trim();
  if (value.endsWith("rem")) return (parseFloat(value) || 1.5) * font;
  if (value.endsWith("px")) return parseFloat(value) || 1.5 * font;
  return 1.5 * font;
}
